// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Selections Page with Tabbed Interface

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { SelectionsPanel } from '@/components/breeding/SelectionsPanel';
import { SelectionDialog } from '@/components/breeding/SelectionDialog';
import { DeleteSelectionDialog } from '@/components/breeding/DeleteSelectionDialog';
import { SelectionComparison } from '@/components/breeding/SelectionComparison';
import { EnhancedSelection } from '@/types/selection';
import { Plant } from '@/types/plant';
import { ExistingSelectionsTable } from '@/components/selections/ExistingSelectionsTable';

export default function SelectionsPage() {
  const { projectId = '' } = useParams<{ projectId: string }>();
  
  // State for managing dialogs and tabs
  const [activeTab, setActiveTab] = useState('basic');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState<Plant | null>(null);
  const [selectedSelection, setSelectedSelection] = useState<EnhancedSelection | null>(null);
  const [selectionsToCompare, setSelectionsToCompare] = useState<EnhancedSelection[]>([]);
  
  // Handle adding new selection
  const handleAddSelection = (plant?: Plant) => {
    if (plant) {
      setSelectedPlant(plant);
    }
    setIsAddDialogOpen(true);
  };
  
  // Handle editing selection
  const handleEditSelection = (selection: EnhancedSelection) => {
    setSelectedSelection(selection);
    setIsEditDialogOpen(true);
  };
  
  // Handle deleting selection
  const handleDeleteSelection = (selection: EnhancedSelection) => {
    setSelectedSelection(selection);
    setIsDeleteDialogOpen(true);
  };
  
  // Handle comparison
  const handleCompareSelections = (selections: EnhancedSelection[]) => {
    setSelectionsToCompare(selections);
    setIsComparisonOpen(true);
  };
  
  // Handle selection success
  const handleSelectionSuccess = () => {
    // Close dialogs
    setIsAddDialogOpen(false);
    setIsEditDialogOpen(false);
    setIsDeleteDialogOpen(false);
    
    // Reset selected items
    setSelectedPlant(null);
    setSelectedSelection(null);
    
    // You would typically refresh data here
    // For example, call a function to refetch selections
  };
  
  return (
    <div className="container py-4">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Selections</h1>
        <Button 
          onClick={() => handleAddSelection()} 
          className="bg-primary"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Selection
        </Button>
      </div>
      
      {/* Tabs for different views */}
      <Tabs 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="mb-4">
          <TabsTrigger value="basic">Basic View</TabsTrigger>
          <TabsTrigger value="enhanced">Enhanced View</TabsTrigger>
        </TabsList>
        
        {/* Basic View (Your existing table) */}
        <TabsContent value="basic">
          <ExistingSelectionsTable 
            projectId={projectId}
            onAddSelection={handleAddSelection} 
          />
        </TabsContent>
        
        {/* Enhanced View (Our new components) */}
        <TabsContent value="enhanced">
          <SelectionsPanel
            projectId={projectId}
            onCreateSelection={() => handleAddSelection()}
            onEditSelection={handleEditSelection}
            onDeleteSelection={handleDeleteSelection}
            onCompareSelections={handleCompareSelections}
            onViewSelectionDetails={(selection) => {
              // Implement view details functionality
              console.log("View details for", selection);
            }}
          />
        </TabsContent>
      </Tabs>
      
      {/* Add Selection Dialog */}
      {isAddDialogOpen && (
        <SelectionDialog
          isOpen={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          plant={selectedPlant || {
            id: "",
            plant_id: "",
            phenotype_id: "",
            project_id: projectId,
            status: "active" as const,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }}
          projectId={projectId}
          onSuccess={handleSelectionSuccess}
        />
      )}
      
      {/* Edit Selection Dialog */}
      {isEditDialogOpen && selectedSelection && (
        <SelectionDialog
          isOpen={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          selection={selectedSelection}
          plant={{
            id: selectedSelection.id,
            plant_id: selectedSelection.plantId,
            phenotype_id: selectedSelection.phenoId,
            project_id: projectId,
            status: "active" as const,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }}
          projectId={projectId}
          onSuccess={handleSelectionSuccess}
        />
      )}
      
      {/* Delete Selection Dialog */}
      {isDeleteDialogOpen && selectedSelection && (
        <DeleteSelectionDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          selection={selectedSelection}
          onSuccess={handleSelectionSuccess}
        />
      )}
      
      {/* Comparison Modal */}
      {isComparisonOpen && selectionsToCompare.length > 0 && (
        <SelectionComparison
          selections={selectionsToCompare}
          onClose={() => setIsComparisonOpen(false)}
          onRemoveSelection={(selection) => {
            setSelectionsToCompare(prev => 
              prev.filter(s => s.id !== selection.id)
            );
            if (selectionsToCompare.length <= 2) {
              setIsComparisonOpen(false);
            }
          }}
        />
      )}
    </div>
  );
} 